import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLPageHead } from '../../music24/components/SMLPageHead/SMLPageHead';
import { SMLSettingsTabDef, SMLSettingsTabId } from '../tabs/types';
import { settingsTabHeadMessages } from '../tabs/settingsTabHeadMessages';
import { SettingsSubtitleStack } from './SettingsSubtitleStack';

interface Props {
  tabs: SMLSettingsTabDef[];
  requestedTab: SMLSettingsTabId | null;
  activeTab: SMLSettingsTabId;
}

/** The head of one tab: "Settings / <tab>", the title and the subtitle of the tab. */
const TabHead: React.FC<{ tabs: SMLSettingsTabDef[]; tab: SMLSettingsTabDef }> = ({ tabs, tab }) => {
  const intl = useIntl();
  const breadcrumbs = useMemo(
    () => [{ name: intl.formatMessage(settingsTabHeadMessages.settings) }, { name: intl.formatMessage(tab.label) }],
    [intl, tab.label]
  );
  return (
    <SMLPageHead
      breadcrumbs={breadcrumbs}
      title={intl.formatMessage(tab.title ?? tab.label)}
      subtitle={<SettingsSubtitleStack tabs={tabs} activeTab={tab.id} />}
    />
  );
};

/** The head of the phone index, where no tab is open yet. */
const IndexHead: React.FC = () => {
  const intl = useIntl();
  const settings = intl.formatMessage(settingsTabHeadMessages.settings);
  const breadcrumbs = useMemo(() => [{ name: settings }], [settings]);
  return (
    <SMLPageHead
      breadcrumbs={breadcrumbs}
      title={settings}
      subtitle={intl.formatMessage(settingsTabHeadMessages.pageSubtitle)}
    />
  );
};

/**
 * The page head of Settings, the same `SMLPageHead` as the other app pages. The breadcrumbs follow
 * the open tab ("Settings / Team"), and the subtitle is the description of the tab, so a tab body
 * repeats neither. With no `?tab=`, the phone shows the index (its own head) and md up shows the
 * first tab.
 */
const SettingsPageHeadComponent: React.FC<Props> = ({ tabs, requestedTab, activeTab }) => {
  const tab = tabs.find((t) => t.id === activeTab);

  if (requestedTab !== null && tab !== undefined) {
    return <TabHead tabs={tabs} tab={tab} />;
  }

  return (
    <>
      <div className="d-md-none" data-testid="settings-index-head">
        <IndexHead />
      </div>
      {tab !== undefined && (
        <div className="d-none d-md-block">
          <TabHead tabs={tabs} tab={tab} />
        </div>
      )}
    </>
  );
};

export const SettingsPageHead = TypedMemo(SettingsPageHeadComponent);
